import { useEffect, useState } from 'react';
import './HvnlyNabAlert.css';

/**
 * HvnlyNabAlert - Professional Alert System for Havenlytics Nab Plugin
 * 
 * USAGE:
 * 1. Import and include this component in your main App/Layout
 * 2. Use the global window.HvnlyNabAlert methods anywhere in your plugin
 * 
 * EXAMPLE USAGE:
 * 
 * // Simple alerts
 * window.HvnlyNabAlert.success('Operation completed successfully!');
 * window.HvnlyNabAlert.error('Something went wrong!');
 * window.HvnlyNabAlert.warning('This action cannot be undone');
 * window.HvnlyNabAlert.info('New features available');
 * 
 * // Confirm dialog (returns promise)
 * window.HvnlyNabAlert.confirm('Are you sure?').then(result => {
 *   if (result === 'confirm') {
 *     // User confirmed
 *   }
 * });
 * 
 * // Prompt for input
 * window.HvnlyNabAlert.prompt('Enter your name:').then(result => {
 *   if (result !== 'cancel') {
 *     console.log('User entered:', result);
 *   }
 * });
 * 
 * // Custom alert with advanced options
 * window.HvnlyNabShowAlert({
 *   title: 'Custom Alert',
 *   message: 'This is a custom alert',
 *   icon: 'success',
 *   autoClose: 3000,
 *   buttons: [
 *     { text: 'Cancel', type: 'cancel', action: 'cancel' },
 *     { text: 'OK', type: 'success', action: 'ok' }
 *   ]
 * });
 */
/**
 * HvnlyNabAlert - Professional Alert System with Smooth Animations
 */


const HvnlyNabAlert = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState(null);
  const [resolvePromise, setResolvePromise] = useState(null);
  const [shouldShake, setShouldShake] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    
    window.HvnlyNabShowAlert = (config) => {
      return new Promise((resolve) => {
        setAlertConfig(config);
        setIsOpen(true);
        setResolvePromise(() => resolve);
        
        // Add shake effect for error alerts
        if (config.icon === 'error') {
          setTimeout(() => {
            setShouldShake(true);
            setTimeout(() => setShouldShake(false), 500);
          }, 600);
        }
      });
    };

    window.HvnlyNabAlert = {
      success: (message, title = 'Success!') => 
        window.HvnlyNabShowAlert({
          title,
          message,
          icon: 'success',
          buttons: [{ text: 'OK', type: 'success', action: 'ok' }]
        }),

      error: (message, title = 'Error!') => 
        window.HvnlyNabShowAlert({
          title,
          message,
          icon: 'error',
          buttons: [{ text: 'OK', type: 'error', action: 'ok' }]
        }),

      warning: (message, title = 'Warning!') => 
        window.HvnlyNabShowAlert({
          title,
          message,
          icon: 'warning',
          buttons: [{ text: 'OK', type: 'warning', action: 'ok' }]
        }),

      info: (message, title = 'Information') => 
        window.HvnlyNabShowAlert({
          title,
          message,
          icon: 'info',
          buttons: [{ text: 'OK', type: 'info', action: 'ok' }]
        }),

      confirm: (message, title = 'Confirm Action') => 
        window.HvnlyNabShowAlert({
          title,
          message,
          icon: 'question',
          allowOutsideClick: false,
          buttons: [
            { text: 'Cancel', type: 'cancel', action: 'cancel' },
            { text: 'Confirm', type: 'success', action: 'confirm' }
          ]
        }),

      prompt: (message, defaultValue = '', title = 'Input Required') => 
        window.HvnlyNabShowAlert({
          title,
          message,
          icon: 'info',
          allowOutsideClick: false,
          content: `
            <input type="text" 
                   class="HvnlyNab_alert_input" 
                   placeholder="${message}" 
                   value="${defaultValue}"
                   id="HvnlyNab_prompt_input"
                   autocomplete="off">
          `,
          buttons: [
            { text: 'Cancel', type: 'cancel', action: 'cancel' },
            { text: 'Submit', type: 'success', action: 'submit' }
          ]
        })
    };

    if (window.HvnlyNabOverrideNativeAlerts) {
      window.alert = (message) => window.HvnlyNabAlert.info(message);
      window.confirm = (message) => window.HvnlyNabAlert.confirm(message);
    }

    return () => {
      delete window.HvnlyNabShowAlert;
      delete window.HvnlyNabAlert;
    };
  }, []);

  const handleClose = (action) => {
    setIsOpen(false);
    setShouldShake(false);
    
    if (resolvePromise) {
      let result = action;
      
      if (action === 'submit' && alertConfig?.content?.includes('HvnlyNab_prompt_input')) {
        const input = document.getElementById('HvnlyNab_prompt_input');
        result = input ? input.value : '';
      }
      
      resolvePromise(result);
      setResolvePromise(null);
    }
    
    setTimeout(() => {
      setAlertConfig(null);
    }, 300);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && alertConfig?.allowOutsideClick !== false) {
      handleClose('overlay');
    }
  };

  if (!alertConfig || !isMounted) return null;

  return (
    <div 
      className={`HvnlyNab_alert_overlay ${isOpen ? 'HvnlyNab_alert_overlay--active' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className={`HvnlyNab_alert_container ${isOpen ? 'HvnlyNab_alert_container--active' : ''} ${shouldShake ? 'HvnlyNab_alert_shake' : ''}`}>
        {alertConfig.autoClose && (
          <div 
            className="HvnlyNab_alert_progress" 
            style={{ 
              animationDuration: `${alertConfig.autoClose}ms`
            }}
          />
        )}
        
        <div className="HvnlyNab_alert_header">
          <div className={`HvnlyNab_alert_icon HvnlyNab_alert_icon--${alertConfig.icon}`}>
            <i className={`fas ${getIconClass(alertConfig.icon)}`}></i>
          </div>
          <h2 className="HvnlyNab_alert_title">{alertConfig.title}</h2>
          <p className="HvnlyNab_alert_message">{alertConfig.message}</p>
        </div>
        
        {alertConfig.content && (
          <div 
            className="HvnlyNab_alert_content"
            dangerouslySetInnerHTML={{ __html: alertConfig.content }}
          />
        )}
        
        <div className="HvnlyNab_alert_footer">
          {alertConfig.buttons?.map((button, index) => (
            <button
              key={index}
              className={`HvnlyNab_alert_btn HvnlyNab_alert_btn--${button.type}`}
              onClick={() => handleClose(button.action)}
              autoFocus={index === alertConfig.buttons.length - 1}
            >
              {button.text}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const getIconClass = (iconType) => {
  const icons = {
    success: 'fa-check-circle',
    error: 'fa-exclamation-circle',
    warning: 'fa-exclamation-triangle',
    info: 'fa-info-circle',
    question: 'fa-question-circle'
  };
  return icons[iconType] || icons.info;
};

export default HvnlyNabAlert;